import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { supabase } from '../services/supabase';
import { sanitizeStoragePath, sanitizeFileExt } from '../utils/storagePath';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import {
  ChatBubbleLeftRightIcon,
  XMarkIcon,
  PaperAirplaneIcon,
  HashtagIcon,
  MegaphoneIcon,
  FaceSmileIcon,
  GifIcon,
  ArrowUturnLeftIcon,
  ChevronLeftIcon,
  PhotoIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import AmaFeatureDemo from './AmaFeatureDemo';

const chatAnimationStyles = `
@keyframes fadeSlideIn {
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes msgIn {
  from { opacity: 0; transform: translateY(12px) scale(0.98); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}
`;

// Avatar that gracefully falls back to a gradient initials bubble if the image
// fails to load (dead/legacy storage URL, 404, blocked host). Without this the
// browser renders its broken-image "?" glyph in the chat.
function ResilientAvatar({ src, name, imgClass, fallbackClass, onClick, title }: {
  src?: string;
  name: string;
  imgClass: string;
  fallbackClass: string;
  onClick?: () => void;
  title?: string;
}) {
  const [failed, setFailed] = useState(false);
  if (src && !failed) {
    return (
      <img
        src={src}
        alt={name}
        title={title}
        onClick={onClick}
        onError={() => setFailed(true)}
        className={imgClass}
      />
    );
  }
  return (
    <div title={title} onClick={onClick} className={fallbackClass}>
      {(name || '?').charAt(0).toUpperCase()}
    </div>
  );
}

interface Channel {
  id: string;
  name: string;
  description: string | null;
  channel_type: 'announcements' | 'general' | 'support' | 'dm';
  is_default: boolean;
  talent_id?: string | null;
}

interface Message {
  id: string;
  channel_id: string;
  sender_talent_id: string | null;
  sender_user_id: string | null;
  parent_message_id: string | null;
  content: string;
  message_type: 'text' | 'gif' | 'image' | 'system';
  gif_url: string | null;
  image_url: string | null;
  is_edited: boolean;
  is_deleted: boolean;
  created_at: string;
  sender_name?: string;
  sender_avatar?: string;
  sender_role?: string;
  reply_count?: number;
}

interface Reaction {
  emoji: string;
  count: number;
  reacted: boolean;
}

interface GiphyGif {
  id: string;
  title: string;
  images: {
    fixed_height: { url: string; width: string; height: string };
    fixed_height_small: { url: string; width: string; height: string };
    original: { url: string };
  };
}

/**
 * How many replies a collapsed thread shows inline. The initial channel load
 * only fetches this many per thread, so anything beyond it lives behind the
 * "View N more replies" button (which fetches the whole thread).
 */
const PREVIEW_REPLY_COUNT = 2;

interface CreatorChatProps {
  talentId: string;
  talentName: string;
  talentAvatar?: string;
  isAdmin?: boolean;
  adminUserId?: string;
  canMentionEveryone?: boolean;
  initialChannelId?: string | null;
  initialOpen?: boolean;
  onNavigateToDesign?: () => void;
}

// Sourced from env (do not hard-code — the previously committed key must be rotated in the Giphy dashboard)
const GIPHY_API_KEY = process.env.REACT_APP_GIPHY_API_KEY || '';
const EMOJI_LIST = ['👍', '❤️', '😂', '🔥', '👏', '🙌', '💯', '🎉', '😍', '🤔', '👀', '💪', '🚀', '⭐', '😢', '😮'];

const ADMIN_ICON_URL = '/whiteicon.png';
const AMA_LAUNCH_ANNOUNCEMENT_ID = 'b8106b86-a252-4189-96bf-3c2d3b73d911';

const SOCIAL_URLS: Record<string, (handle: string) => string> = {
  twitter: h => `https://x.com/${h}`,
  instagram: h => `https://instagram.com/${h}`,
  tiktok: h => `https://tiktok.com/@${h}`,
  youtube: h => `https://youtube.com/@${h}`,
  facebook: h => `https://facebook.com/${h}`,
  snapchat: h => `https://snapchat.com/add/${h}`,
  rumble: h => `https://rumble.com/c/${h}`,
};

const SOCIAL_ICONS: Record<string, string> = {
  twitter: '𝕏', instagram: '📸', tiktok: '♪', youtube: '▶', facebook: 'f', snapchat: '👻', rumble: '🎬',
};

interface ProfileCard {
  talentId: string;
  name: string;
  avatar?: string;
  bio?: string;
  slug?: string;
  socials: { platform: string; handle: string }[];
}

export default function CreatorChat({ talentId, talentName, talentAvatar, isAdmin, adminUserId, canMentionEveryone = false, initialChannelId, initialOpen, onNavigateToDesign }: CreatorChatProps) {
  const [isOpen, setIsOpen] = useState(initialOpen || false);
  const [channels, setChannels] = useState<Channel[]>([]);
  const [activeChannel, setActiveChannel] = useState<Channel | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [messageInput, setMessageInput] = useState('');
  const [sending, setSending] = useState(false);
  const [unreadMentions, setUnreadMentions] = useState(0);
  const [lastAnnouncement, setLastAnnouncement] = useState<Message | null>(null);
  const [showAnnouncementPreview, setShowAnnouncementPreview] = useState(false);
  const [threadParent, setThreadParent] = useState<Message | null>(null);
  const [threadMessages, setThreadMessages] = useState<Message[]>([]);
  const [expandedThreads, setExpandedThreads] = useState<Set<string>>(new Set());
  const [threadReplies, setThreadReplies] = useState<Record<string, Message[]>>({});
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showGifPicker, setShowGifPicker] = useState(false);
  const [gifSearch, setGifSearch] = useState('');
  const [gifResults, setGifResults] = useState<GiphyGif[]>([]);
  const [gifLoading, setGifLoading] = useState(false);
  const [reactions, setReactions] = useState<Record<string, Reaction[]>>({});
  const [showReactionPicker, setShowReactionPicker] = useState<string | null>(null);
  const [senderNames, setSenderNames] = useState<Record<string, { name: string; avatar?: string; role?: string }>>({});
  const [showMobileSidebar, setShowMobileSidebar] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [longPressMsg, setLongPressMsg] = useState<string | null>(null);
  const [channelMemberCount, setChannelMemberCount] = useState(0);
  const [channelMemberAvatars, setChannelMemberAvatars] = useState<{ name: string; avatar?: string }[]>([]);
  const [channelBadges, setChannelBadges] = useState<Record<string, number>>({});
  const [channelPreviews, setChannelPreviews] = useState<Record<string, { content: string; sender: string; created_at: string }>>({});
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [mentionSearch, setMentionSearch] = useState<string | null>(null);
  const [mentionResults, setMentionResults] = useState<{ id: string; name: string }[]>([]);
  const [channelMentionSearch, setChannelMentionSearch] = useState<string | null>(null);
  const [channelMentionResults, setChannelMentionResults] = useState<{ id: string; name: string; channel_type: string }[]>([]);
  const [channelTalentList, setChannelTalentList] = useState<{ id: string; name: string }[]>([]);
  const [profileCard, setProfileCard] = useState<ProfileCard | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const [chatBanned, setChatBanned] = useState(false);

  const closeChat = useCallback(() => {
    setIsOpen(false);
    setReplyingTo(null);
    setMessageInput('');
    setShowGifPicker(false);
    setShowEmojiPicker(false);
    setShowReactionPicker(null);
    setThreadParent(null);
  }, []);

  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const threadEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const touchStartX = useRef<number>(0);
  const touchStartY = useRef<number>(0);
  const senderNamesRef = useRef(senderNames);
  senderNamesRef.current = senderNames;
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  const senderId = isAdmin ? adminUserId : talentId;
  const senderType = isAdmin ? 'user' : 'talent';

  // Load (and stay subscribed to) the creator's chat-ban flag so the input
  // is disabled the moment an admin flips the switch — and re-enabled the
  // moment they flip it back. Admins are exempt from the client-side ban UX
  // (they post as user, not talent, and the server-side trigger keys off
  // sender_talent_id only).
  useEffect(() => {
    if (isAdmin || !talentId) return;
    let cancelled = false;
    const loadBan = async () => {
      const { data } = await supabase
        .from('talent_profiles')
        .select('chat_banned_at')
        .eq('id', talentId)
        .maybeSingle();
      if (!cancelled) setChatBanned(!!data?.chat_banned_at);
    };
    loadBan();
    const channel = supabase
      .channel(`talent-chat-ban-${talentId}`)
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'talent_profiles',
        filter: `id=eq.${talentId}`,
      }, (payload: any) => {
        setChatBanned(!!payload?.new?.chat_banned_at);
      })
      .subscribe();
    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
    };
  }, [isAdmin, talentId]);

  /**
   * Pin the message list to the newest message.
   *
   * Writes `scrollTop` on the scroll container rather than calling
   * `scrollIntoView()` on a trailing sentinel. `scrollIntoView` walks ancestors
   * (this panel is portalled over a `position: fixed` body — see the viewport
   * lock below) and, more importantly, is a silent no-op whenever the container
   * is `display: none`. That last one is the mobile first-open case: the message
   * pane sits hidden behind the channel list while the history loads, so the one
   * scroll attempt lands nowhere and the pane un-hides at the OLDEST message.
   */
  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth', force = false) => {
    const pin = (b: ScrollBehavior, onlyIfNearBottom: boolean) => {
      const el = messagesContainerRef.current;
      // Hidden or not laid out yet — scrolling now would do nothing.
      if (!el || el.clientHeight === 0) return false;
      if (onlyIfNearBottom && el.scrollHeight - el.scrollTop - el.clientHeight > 400) return true;
      // Plain assignment for the instant case — it can't be ignored by an older
      // WebView the way the options form of scrollTo() can.
      if (b === 'smooth') el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
      else el.scrollTop = el.scrollHeight;
      return true;
    };
    requestAnimationFrame(() => {
      const laidOut = pin(behavior, false);
      // Follow-ups are scheduled even when that first attempt found the pane
      // unlaid-out, otherwise a container that mounts at clientHeight 0 never
      // gets pinned at all and the reader lands on the OLDEST message.
      //
      // `force` is what opening a channel passes. Without it the follow-ups are
      // near-bottom-gated, and on first load that gate is always shut: the first
      // pin runs while the list is still a loading stub, then the messages render
      // and the now-tall list reads as "reader has scrolled far up", so every
      // retry declines to move and the channel opens at the top. Incremental
      // calls (a message arriving) keep the gate so we never yank a reader who
      // genuinely scrolled back through history.
      const gate = !force && laidOut;
      [80, 250, 600].forEach(ms => setTimeout(() => pin('auto', gate), ms));
    });
  }, []);

  /**
   * Mobile opens onto the channel list, so the message pane is `display: none`
   * while a channel's history loads and the load's own scroll can't take. The
   * pane also stays mounted at whatever offset it had, so tapping back into the
   * SAME channel doesn't re-run the loader. Re-pin whenever the pane goes
   * visible with a loaded channel behind it.
   */
  useEffect(() => {
    if (showMobileSidebar || loadingMessages || !activeChannel) return;
    scrollToBottom('auto', true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showMobileSidebar, loadingMessages, activeChannel?.id]);

  // Lock viewport zoom when chat is open to prevent iOS keyboard zoom glitch
  useEffect(() => {
    const meta = document.querySelector('meta[name="viewport"]');
    if (!meta) return;
    const original = meta.getAttribute('content') || '';
    let savedScrollY = 0;
    if (isOpen) {
      savedScrollY = window.scrollY;
      meta.setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1, minimum-scale=1, user-scalable=no, viewport-fit=cover');
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${savedScrollY}px`;
      document.body.style.width = '100%';
    }
    return () => {
      meta.setAttribute('content', original || 'width=device-width, initial-scale=1, viewport-fit=cover, user-scalable=no');
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, savedScrollY);
    };
  }, [isOpen]);

  // Refresh messages when app returns to foreground or chat reopens
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') setRefreshTick(t => t + 1);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  const wasOpenRef = useRef(isOpen);
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) setRefreshTick(t => t + 1);
    wasOpenRef.current = isOpen;
  }, [isOpen]);

  useEffect(() => {
    if (initialOpen) setIsOpen(true);
  }, [initialOpen]);

  // Load total unread count (runs always, even when chat is closed)
  const loadTotalUnread = useCallback(async () => {
    let total = 0;
    if (isAdmin && adminUserId) {
      const { count } = await supabase.from('chat_mentions').select('id', { count: 'exact', head: true }).eq('mentioned_user_id', adminUserId).eq('is_read', false);
      total += count || 0;
    }
    if (talentId) {
      const { count } = await supabase.from('chat_mentions').select('id', { count: 'exact', head: true }).eq('mentioned_talent_id', talentId).eq('is_read', false);
      total += count || 0;
    }
    setUnreadMentions(total);
  }, [isAdmin, adminUserId, talentId]);

  useEffect(() => {
    loadTotalUnread();
    const interval = setInterval(loadTotalUnread, 15000);
    return () => clearInterval(interval);
  }, [loadTotalUnread]);

  // Load per-channel badges + previews (batched queries instead of N+1)
  const loadChannelBadges = useCallback(async (channelList: Channel[]) => {
    if (!channelList.length) return;
    const channelIds = channelList.map(c => c.id);
    const badges: Record<string, number> = {};
    channelIds.forEach(id => { badges[id] = 0; });

    // Batch fetch all unread mentions across all channels at once
    const mentionResults: { data: any }[] = [];
    if (isAdmin && adminUserId) {
      mentionResults.push(await supabase.from('chat_mentions').select('channel_id').in('channel_id', channelIds).eq('mentioned_user_id', adminUserId).eq('is_read', false));
    }
    if (talentId) {
      mentionResults.push(await supabase.from('chat_mentions').select('channel_id').in('channel_id', channelIds).eq('mentioned_talent_id', talentId).eq('is_read', false));
    }
    mentionResults.forEach(({ data }) => {
      (data || []).forEach((m: any) => { badges[m.channel_id] = (badges[m.channel_id] || 0) + 1; });
    });
    setChannelBadges(badges);

    // Fetch latest message for every channel in a single round-trip (DISTINCT ON)
    // instead of N parallel queries. Falls back to the per-channel approach if
    // the RPC isn't available for some reason.
    const previews: Record<string, { content: string; sender: string; created_at: string }> = {};
    const { data: latestRows, error: latestErr } = await supabase
      .rpc('get_channel_latest_messages', { p_channel_ids: channelIds });
    if (!latestErr && latestRows) {
      (latestRows as any[]).forEach((msg) => {
        const cached = senderNamesRef.current[msg.sender_talent_id || msg.sender_user_id || ''];
        const senderName = cached?.name || (msg.sender_user_id ? 'Admin' : 'Creator');
        previews[msg.channel_id] = {
          content: msg.message_type === 'gif' ? '📎 GIF' : msg.message_type === 'image' ? '📷 Image' : (msg.content?.slice(0, 80) || ''),
          sender: senderName,
          created_at: msg.created_at,
        };
      });
    } else {
      await Promise.all(channelIds.map(async (chId) => {
        const { data: latest } = await supabase
          .from('chat_messages')
          .select('content, message_type, sender_talent_id, sender_user_id, created_at')
          .eq('channel_id', chId)
          .eq('is_deleted', false)
          .is('parent_message_id', null)
          .order('created_at', { ascending: false })
          .limit(1);
        if (latest?.[0]) {
          const msg = latest[0];
          const cached = senderNamesRef.current[msg.sender_talent_id || msg.sender_user_id || ''];
          const senderName = cached?.name || (msg.sender_user_id ? 'Admin' : 'Creator');
          previews[chId] = {
            content: msg.message_type === 'gif' ? '📎 GIF' : msg.message_type === 'image' ? '📷 Image' : (msg.content?.slice(0, 80) || ''),
            sender: senderName,
            created_at: msg.created_at,
          };
        }
      }));
    }
    setChannelPreviews(previews);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, adminUserId, talentId]);

  const loadChannels = useCallback(async () => {
    // Kick off the three "lookup" queries in parallel so we don't block the
    // sidebar on 3 sequential round-trips.
    const adminMembersP = (isAdmin && adminUserId)
      ? supabase.from('chat_channel_members').select('channel_id').eq('user_id', adminUserId)
      : Promise.resolve({ data: [] as { channel_id: string }[] });
    const talentMembersP = talentId
      ? supabase.from('chat_channel_members').select('channel_id').eq('talent_id', talentId)
      : Promise.resolve({ data: [] as { channel_id: string }[] });
    const supportP = talentId
      ? supabase.from('chat_channels').select('*').eq('channel_type', 'support').eq('talent_id', talentId)
      : Promise.resolve({ data: [] as any[] });

    const [adminRes, talentRes, supportRes] = await Promise.all([adminMembersP, talentMembersP, supportP]);

    const channelIds: string[] = [];
    (adminRes.data || []).forEach((m: any) => { if (!channelIds.includes(m.channel_id)) channelIds.push(m.channel_id); });
    (talentRes.data || []).forEach((m: any) => { if (!channelIds.includes(m.channel_id)) channelIds.push(m.channel_id); });
    (supportRes.data || []).forEach((sc: any) => { if (!channelIds.includes(sc.id)) channelIds.push(sc.id); });

    if (!channelIds.length) {
      setChannels([]);
      loadChannelBadges([]);
      return;
    }
    const { data } = await supabase.from('chat_channels').select('*').in('id', channelIds).order('created_at');
    if (data) {
      setChannels(data);
      loadChannelBadges(data);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, adminUserId, talentId]);

  useEffect(() => {
    if (!isOpen) return;
    loadChannels();
    if (Capacitor.isNativePlatform()) {
      PushNotifications.removeAllDeliveredNotifications().catch(() => {});
    }
  }, [isOpen, loadChannels, refreshTick]);

  // Subscribe to new channel memberships so new channels appear automatically
  useEffect(() => {
    if (!isOpen) return;
    const subs: ReturnType<typeof supabase.channel>[] = [];
    if (isAdmin && adminUserId) {
      const sub = supabase.channel(`channel-members-admin-${adminUserId}`)
        .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'chat_channel_members', filter: `user_id=eq.${adminUserId}` },
          () => { loadChannels(); })
        .subscribe();
      subs.push(sub);
    }
    if (talentId) {
      const sub = supabase.channel(`channel-members-talent-${talentId}`)
        .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'chat_channel_members', filter: `talent_id=eq.${talentId}` },
          () => { loadChannels(); })
        .subscribe();
      subs.push(sub);
    }
    return () => { subs.forEach(s => supabase.removeChannel(s)); };
  }, [isOpen, isAdmin, adminUserId, talentId, loadChannels]);

  // Periodically re-fetch channels so new ones appear even if Realtime misses
  useEffect(() => {
    if (!isOpen) return;
    const interval = setInterval(loadChannels, 30000);
    return () => clearInterval(interval);
  }, [isOpen, loadChannels]);

  // Refresh badges periodically
  useEffect(() => {
    if (channels.length === 0) return;
    const interval = setInterval(() => loadChannelBadges(channels), 30000);
    return () => clearInterval(interval);
  }, [channels, loadChannelBadges]);

  // Deep link: open to a specific channel from push notification or Network tab
  const prevInitialChannelIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!channels.length) return;
    if (initialChannelId) {
      // Always navigate when initialChannelId changes to a new value
      if (initialChannelId !== prevInitialChannelIdRef.current || !activeChannel) {
        prevInitialChannelIdRef.current = initialChannelId;
        const target = channels.find(c => c.id === initialChannelId);
        if (target) {
          setActiveChannel(target);
          setShowMobileSidebar(false);
          setIsOpen(true);
        }
      }
    } else if (!activeChannel) {
      // Default to #creators, falling back to the first non-support channel
      const creators = channels.find(c => c.name.toLowerCase() === 'creators');
      const fallback = channels.find(c => c.channel_type !== 'support');
      const def = creators || fallback || channels[0];
      if (def) setActiveChannel(def);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialChannelId, channels]);

  // Load last announcement for preview bubble
  useEffect(() => {
    const loadLastAnnouncement = async () => {
      const { data: announcementChannel } = await supabase
        .from('chat_channels')
        .select('id')
        .eq('channel_type', 'announcements')
        .eq('is_default', true)
        .single();
      if (!announcementChannel) return;

      const { data: msgs } = await supabase
        .from('chat_messages')
        .select('*')
        .eq('channel_id', announcementChannel.id)
        .eq('is_deleted', false)
        .is('parent_message_id', null)
        .order('created_at', { ascending: false })
        .limit(1);

      if (msgs?.[0]) {
        setLastAnnouncement(msgs[0]);
        const memberFilter = isAdmin
          ? supabase.from('chat_channel_members').select('last_read_at').eq('channel_id', announcementChannel.id).eq('user_id', adminUserId).single()
          : supabase.from('chat_channel_members').select('last_read_at').eq('channel_id', announcementChannel.id).eq('talent_id', talentId).single();
        const { data: member } = await memberFilter;
        if (member && new Date(msgs[0].created_at) > new Date(member.last_read_at)) {
          setShowAnnouncementPreview(true);
        }
      }
    };
    loadLastAnnouncement();
  }, [talentId, adminUserId, isAdmin]);

  // Resolve sender names — use ref to avoid dependency loops
  const resolveSender = useCallback(async (msg: Message) => {
    const key = msg.sender_talent_id || msg.sender_user_id || 'unknown';
    if (senderNamesRef.current[key]) return;
    if (msg.sender_talent_id) {
      const { data } = await supabase.from('talent_profiles')
        .select('full_name, temp_full_name, temp_avatar_url, user_id')
        .eq('id', msg.sender_talent_id).single();
      if (data) {
        let isCoreTeam = false;
        let isLead = false;
        if (data.user_id) {
          // Resolve core_team via the SECURITY DEFINER RPC. The anon role is not
          // granted SELECT on users.user_tags, so a direct table read fails
          // whenever this runs before the auth session attaches.
          const { data: info } = await supabase.rpc('get_chat_sender_info', { p_user_ids: [data.user_id] });
          isCoreTeam = Array.isArray(info) && info[0]?.is_core_team === true;
          isLead = Array.isArray(info) && info[0]?.is_lead === true;
        }
        // Lead outranks Core Team: a Lead normally holds both tags, and only one
        // badge is rendered.
        setSenderNames(prev => ({ ...prev, [key]: { name: data.full_name || data.temp_full_name || 'Creator', avatar: data.temp_avatar_url || undefined, role: isLead ? 'lead' : isCoreTeam ? 'core_team' : 'creator' } }));
      }
    } else if (msg.sender_user_id) {
      // Use the SECURITY DEFINER RPC instead of reading users directly: anon
      // lacks SELECT on users.email / users.user_tags, so the old direct query
      // 401'd for unauthenticated/pre-session loads and senders showed as "User".
      const { data: info } = await supabase.rpc('get_chat_sender_info', { p_user_ids: [msg.sender_user_id] });
      const usr = Array.isArray(info) ? info[0] : null;
      if (usr?.is_ai) {
        setSenderNames(prev => ({ ...prev, [key]: { name: 'JAI', avatar: undefined, role: 'jai' } }));
      } else if (usr?.is_admin_persona) {
        // Official "ShoutOut Admin" brand persona only — not every staff account.
        setSenderNames(prev => ({ ...prev, [key]: { name: 'ShoutOut Admin', avatar: ADMIN_ICON_URL, role: 'admin' } }));
      } else if (usr) {
        // Real people (staff posting as themselves, customers, etc.) show as
        // themselves — their own name + avatar, no forced ADMIN badge.
        setSenderNames(prev => ({ ...prev, [key]: { name: usr.full_name || 'User', avatar: usr.avatar_url || undefined, role: usr.is_lead ? 'lead' : usr.is_core_team ? 'core_team' : 'member' } }));
      }
    }
  }, []);

  // Load (or refresh) reactions for a set of messages. Kept separate from the
  // message load so the polling safety net can re-run it — otherwise a slow or
  // failed first fetch left reactions blank until a manual refresh.
  const loadReactions = useCallback(async (msgIds: string[]) => {
    if (!msgIds || msgIds.length === 0) return;
    const { data: rxns } = await supabase
      .from('chat_reactions')
      .select('*')
      .in('message_id', msgIds);
    if (!rxns) return;
    const grouped: Record<string, Reaction[]> = {};
    rxns.forEach(r => {
      if (!grouped[r.message_id]) grouped[r.message_id] = [];
      const existing = grouped[r.message_id].find(e => e.emoji === r.emoji);
      const isMe = isAdmin ? r.user_id === adminUserId : r.talent_id === talentId;
      if (existing) { existing.count++; if (isMe) existing.reacted = true; }
      else grouped[r.message_id].push({ emoji: r.emoji, count: 1, reacted: isMe });
    });
    setReactions(grouped);
  }, [isAdmin, adminUserId, talentId]);

  // Load messages for active channel
  useEffect(() => {
    if (!activeChannel) return;
    setLoadingMessages(true);
    const load = async () => {
      const { data } = await supabase
        .from('chat_messages')
        .select('*')
        .eq('channel_id', activeChannel.id)
        .is('parent_message_id', null)
        .eq('is_deleted', false)
        .order('created_at', { ascending: false })
        .limit(200);

      if (data) {
        const sorted = [...data].reverse();
        setMessages(sorted);
        sorted.forEach(resolveSender);

        // Get reply counts
        const msgIds = sorted.map(m => m.id);
        if (msgIds.length > 0) {
          // Fetch reactions in parallel so the reply-preview round-trips below
          // can't delay (or, on failure, block) reactions from appearing.
          loadReactions(msgIds);
          const { data: replies } = await supabase
            .from('chat_messages')
            .select('parent_message_id')
            .in('parent_message_id', msgIds)
            .eq('is_deleted', false);
          if (replies) {
            const counts: Record<string, number> = {};
            replies.forEach(r => { counts[r.parent_message_id!] = (counts[r.parent_message_id!] || 0) + 1; });
            setMessages(prev => prev.map(m => ({ ...m, reply_count: counts[m.id] || 0 })));

            // Pre-load the newest few replies for messages that have them
            const msgsWithReplies = Object.keys(counts);
            if (msgsWithReplies.length > 0) {
              const previewReplies: Record<string, Message[]> = {};
              await Promise.all(msgsWithReplies.map(async (parentId) => {
                const { data: lastTwo } = await supabase
                  .from('chat_messages')
                  .select('*')
                  .eq('parent_message_id', parentId)
                  .eq('is_deleted', false)
                  .order('created_at', { ascending: false })
                  .limit(PREVIEW_REPLY_COUNT);
                if (lastTwo) {
                  previewReplies[parentId] = lastTwo.reverse();
                  lastTwo.forEach(resolveSender);
                }
              }));
              setThreadReplies(previewReplies);
            }
          }
        }

      }
      setLoadingMessages(false);
      // Opening a channel must land on the newest message, so this pin is forced
      // past the near-bottom gate — the list is still a stub at this point and
      // grows underneath us as the messages render.
      scrollToBottom('auto', true);

      // Mark channel as read (update both user_id and talent_id rows for dual-role users)
      const now = new Date().toISOString();
      if (isAdmin && adminUserId) {
        await supabase.from('chat_channel_members').update({ last_read_at: now }).eq('channel_id', activeChannel.id).eq('user_id', adminUserId);
        await supabase.from('chat_mentions').update({ is_read: true }).eq('channel_id', activeChannel.id).eq('mentioned_user_id', adminUserId).eq('is_read', false);
      }
      if (talentId) {
        await supabase.from('chat_channel_members').update({ last_read_at: now }).eq('channel_id', activeChannel.id).eq('talent_id', talentId);
        await supabase.from('chat_mentions').update({ is_read: true }).eq('channel_id', activeChannel.id).eq('mentioned_talent_id', talentId).eq('is_read', false);
      }

      // Clear badge for this channel and refresh totals
      setChannelBadges(prev => ({ ...prev, [activeChannel.id]: 0 }));
      loadTotalUnread();
      if (Capacitor.isNativePlatform()) {
        PushNotifications.removeAllDeliveredNotifications().catch(() => {});
      }
    };
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeChannel?.id, talentId, adminUserId, isAdmin, refreshTick]);

  // Load channel members (count + avatars for header)
  useEffect(() => {
    if (!activeChannel) return;
    const loadChannelMembers = async () => {
      const { data, count } = await supabase
        .from('chat_channel_members')
        .select('talent_id, user_id', { count: 'exact' })
        .eq('channel_id', activeChannel.id);
      setChannelMemberCount(count || 0);
      if (data) {
        const talentIds = data.filter(m => m.talent_id).map(m => m.talent_id!);
        const adminUserIds = data.filter(m => m.user_id).map(m => m.user_id!);
        const avatars: { name: string; avatar?: string }[] = [];
        if (adminUserIds.length > 0) {
          // Via SECURITY DEFINER RPC (anon lacks SELECT on users.email).
          const { data: adminUsers } = await supabase.rpc('get_chat_sender_info', { p_user_ids: adminUserIds });
          if (Array.isArray(adminUsers)) adminUsers.forEach((u: any) => {
            if (u.is_admin_persona) {
              avatars.push({ name: 'ShoutOut Admin', avatar: ADMIN_ICON_URL });
            } else {
              avatars.push({ name: u.full_name || 'Admin', avatar: u.avatar_url || undefined });
            }
          });
        }
        if (talentIds.length > 0) {
          const { data: profiles } = await supabase
            .from('talent_profiles')
            .select('id, full_name, temp_avatar_url')
            .in('id', talentIds)
            .limit(5);
          if (profiles) {
            profiles.forEach(p => avatars.push({ name: p.full_name || 'Creator', avatar: p.temp_avatar_url || undefined }));
            setChannelTalentList(profiles.map(p => ({ id: p.id, name: p.full_name || 'Creator' })));
          }
        }
        // Load full talent list for @mentions
        if (talentIds.length > 0) {
          const { data: allMembers } = await supabase
            .from('talent_profiles')
            .select('id, full_name')
            .in('id', talentIds);
          if (allMembers) setChannelTalentList(allMembers.map(p => ({ id: p.id, name: p.full_name || 'Creator' })));
        }
        setChannelMemberAvatars(avatars.slice(0, 5));
      }
    };
    loadChannelMembers();
  }, [activeChannel]);

  // Apply an incoming message (from realtime OR the polling fallback) to state.
  const ingestMessage = useCallback((newMsg: Message) => {
    if (!newMsg.parent_message_id) {
      let added = false;
      setMessages(prev => {
        if (prev.some(m => m.id === newMsg.id)) return prev;
        added = true;
        return [...prev, { ...newMsg, reply_count: 0 }];
      });
      if (added) {
        resolveSender(newMsg);
        scrollToBottom();
      }
    } else {
      resolveSender(newMsg);
      setThreadReplies(prev => {
        const existing = prev[newMsg.parent_message_id!] || [];
        if (existing.some(m => m.id === newMsg.id)) return prev;
        return { ...prev, [newMsg.parent_message_id!]: [...existing, newMsg] };
      });
      setMessages(prev => prev.map(m => {
        if (m.id !== newMsg.parent_message_id) return m;
        // Only bump the count if we haven't already accounted for this reply.
        return { ...m, reply_count: (m.reply_count || 0) + 1 };
      }));
    }
  }, [resolveSender, scrollToBottom]);

  // Realtime subscription for new messages (stable — no message dep).
  // Re-subscribes automatically if the socket errors/times-out/closes so the
  // chat keeps updating without a manual page refresh.
  useEffect(() => {
    if (!activeChannel) return;
    const channelId = activeChannel.id;
    let cancelled = false;
    let sub: ReturnType<typeof supabase.channel> | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    const connect = () => {
      if (cancelled) return;
      sub = supabase
        .channel(`chat-${channelId}-${Date.now()}`)
        .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'chat_messages', filter: `channel_id=eq.${channelId}` },
          (payload) => ingestMessage(payload.new as Message)
        )
        .subscribe((status) => {
          if (cancelled) return;
          if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
            // Reconnect after a short backoff.
            if (sub) { supabase.removeChannel(sub); sub = null; }
            if (retryTimer) clearTimeout(retryTimer);
            retryTimer = setTimeout(connect, 2000);
          }
        });
    };
    connect();

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      if (sub) supabase.removeChannel(sub);
    };
  }, [activeChannel, ingestMessage, refreshTick]);

  // Polling safety net: even if the realtime socket silently drops, fetch any
  // messages newer than what we already have every few seconds so new messages
  // always appear without a manual refresh. Dedupe is handled by ingestMessage.
  useEffect(() => {
    if (!activeChannel) return;
    const channelId = activeChannel.id;
    let stopped = false;

    const poll = async () => {
      if (stopped || document.visibilityState !== 'visible') return;
      const current = messagesRef.current;
      const latestTs = current.length > 0
        ? current.reduce((max, m) => (m.created_at > max ? m.created_at : max), current[0].created_at)
        : null;
      let query = supabase
        .from('chat_messages')
        .select('*')
        .eq('channel_id', channelId)
        .is('parent_message_id', null)
        .eq('is_deleted', false)
        .order('created_at', { ascending: true })
        .limit(50);
      if (latestTs) query = query.gt('created_at', latestTs);
      const { data } = await query;
      if (!stopped && data && data.length > 0) data.forEach(ingestMessage);
      // Also refresh reactions here so they self-heal if the initial fetch on
      // first open missed them (the reported "reactions blank until refresh").
      if (!stopped) loadReactions(messagesRef.current.map(m => m.id));
    };

    const interval = setInterval(poll, 7000);
    return () => { stopped = true; clearInterval(interval); };
  }, [activeChannel, ingestMessage, loadReactions]);

  // Load thread messages (inline expand)
  const openThread = useCallback(async (msg: Message) => {
    const msgId = msg.id;
    if (expandedThreads.has(msgId)) {
      setExpandedThreads(prev => { const s = new Set(prev); s.delete(msgId); return s; });
      setReplyingTo(prev => prev === msgId ? null : prev);
      return;
    }
    const { data } = await supabase
      .from('chat_messages')
      .select('*')
      .eq('parent_message_id', msgId)
      .eq('is_deleted', false)
      .order('created_at', { ascending: true });
    if (data) {
      setThreadReplies(prev => ({ ...prev, [msgId]: data }));
      data.forEach(resolveSender);
    }
    setExpandedThreads(prev => new Set(prev).add(msgId));
    setReplyingTo(msgId);
  }, [expandedThreads, resolveSender]);

  // Create or get support channel for this creator
  const getOrCreateSupportChannel = async (): Promise<Channel | null> => {
    const existing = channels.find(c => c.channel_type === 'support' && c.talent_id === talentId);
    if (existing) return existing;
    const { data: dbExisting } = await supabase.from('chat_channels')
      .select('*').eq('channel_type', 'support').eq('talent_id', talentId).single();
    if (dbExisting) {
      setChannels(prev => prev.some(c => c.id === dbExisting.id) ? prev : [...prev, dbExisting]);
      return dbExisting;
    }
    const { data: created, error } = await supabase.from('chat_channels').insert({
      name: `Support — ${talentName}`, channel_type: 'support', talent_id: talentId,
    }).select().single();
    if (error || !created) { toast.error('Failed to create support channel'); return null; }
    // Add talent + admin as members
    const { data: adminUser } = await supabase.from('users').select('id').eq('email', 'admin@shoutout.us').single();
    const members: any[] = [{ channel_id: created.id, talent_id: talentId }];
    if (adminUser) members.push({ channel_id: created.id, user_id: adminUser.id });
    await supabase.from('chat_channel_members').insert(members);
    setChannels(prev => [...prev, created]);
    return created;
  };

  // Upload image (support channel only)
  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !activeChannel || activeChannel.channel_type !== 'support') return;
    if (chatBanned && !isAdmin) {
      toast.error("You're banned from chat. Contact ShoutOut support.");
      if (e.target) e.target.value = '';
      return;
    }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5MB'); return; }
    setUploadingImage(true);
    const ext = sanitizeFileExt(file.name, 'jpg');
    const path = `${sanitizeStoragePath(activeChannel.id)}/${Date.now()}.${ext}`;
    const { error: uploadErr } = await supabase.storage.from('chat-images').upload(path, file);
    if (uploadErr) { toast.error('Upload failed'); setUploadingImage(false); return; }
    const { data: urlData } = supabase.storage.from('chat-images').getPublicUrl(path);
    const msgData: any = {
      channel_id: activeChannel.id, content: file.name, message_type: 'image',
      image_url: urlData.publicUrl,
    };
    if (isAdmin) msgData.sender_user_id = adminUserId;
    else msgData.sender_talent_id = talentId;
    const imgThreadParent = replyingTo ? resolveThreadParentId(replyingTo) : null;
    if (imgThreadParent) msgData.parent_message_id = imgThreadParent;
    const { error: imgInsertErr } = await supabase.from('chat_messages').insert(msgData);
    if (imgInsertErr && String(imgInsertErr.message || '').toLowerCase().includes('chat_banned')) {
      setChatBanned(true);
      toast.error("You're banned from chat. Contact ShoutOut support.");
    }
    setReplyingTo(null);
    setUploadingImage(false);
    if (e.target) e.target.value = '';
  };

  // Send message (handles both new messages and replies via replyingTo state)
  // Resolve the top-level parent for threading: if replyingTo is itself a reply, use its parent
  const resolveThreadParentId = (replyToId: string): string => {
    const allReplies = Object.values(threadReplies).flat();
    const replyMsg = allReplies.find(r => r.id === replyToId);
    return replyMsg?.parent_message_id || replyToId;
  };

  const triggerPushNotification = async (channelId: string, senderName: string, content: string, parentMessageId?: string | null) => {
    try {
      // ── Reply notification ──
      if (parentMessageId) {
        const { data: parentMsg } = await supabase.from('chat_messages')
          .select('sender_talent_id, reply_push_sent').eq('id', parentMessageId).single();
        if (parentMsg?.sender_talent_id && parentMsg.sender_talent_id !== talentId && !parentMsg.reply_push_sent) {
          supabase.from('chat_messages').update({ reply_push_sent: true }).eq('id', parentMessageId).then(() => {});
          supabase.functions.invoke('send-push-notification', {
            body: { talentIds: [parentMsg.sender_talent_id], title: 'ShoutOut', body: `${senderName} commented on your message`, channel_id: channelId },
          }).catch(() => {});
        }
      }

      // ── @everyone broadcast ──
      const hasEveryone = canMentionEveryone && /@everyone\b/.test(content);
      if (hasEveryone) {
        const { data: members } = await supabase
          .from('chat_channel_members')
          .select('talent_id')
          .eq('channel_id', channelId);
        const allIds = (members || [])
          .map((m: any) => m.talent_id)
          .filter(Boolean)
          .filter((id: string) => id !== talentId);
        if (allIds.length > 0) {
          supabase.functions.invoke('send-push-notification', {
            body: {
              talentIds: allIds,
              title: 'ShoutOut',
              body: `${senderName}: ${content.replace(/@everyone\b/g, '').replace(/\s+/g, ' ').trim().slice(0, 120) || 'New announcement'}`,
              channel_id: channelId,
            },
          }).catch(() => {});
        }
      }

      // ── Individual @mentions ──
      const mentionTargets: string[] = [];
      const mentionMatches = content.match(/@(\w+)/g);
      if (mentionMatches) {
        const handles = mentionMatches.map(m => m.slice(1).toLowerCase()).filter(h => h !== 'everyone');
        for (const handle of handles) {
          const match = channelTalentList.find(t => t.name.replace(/\s/g, '').toLowerCase() === handle);
          if (match && match.id !== talentId && !mentionTargets.includes(match.id)) mentionTargets.push(match.id);
        }
        if (handles.length > 0) {
          const { data: mentioned } = await supabase.from('talent_profiles')
            .select('id').or(handles.map(h => `username.ilike.${h}`).join(','));
          if (mentioned) mentioned.forEach(t => { if (t.id !== talentId && !mentionTargets.includes(t.id)) mentionTargets.push(t.id); });
        }
      }
      if (mentionTargets.length > 0) {
        supabase.functions.invoke('send-push-notification', {
          body: { talentIds: mentionTargets, title: 'ShoutOut', body: `${senderName} tagged you`, channel_id: channelId },
        }).catch(() => {});
      }
    } catch {}
  };

  const sendMessage = async () => {
    const text = messageInput.trim();
    if (!text || sending) return;
    if (/@everyone\b/.test(text) && !canMentionEveryone) {
      toast.error('You don\'t have permission to @everyone');
      return;
    }
    if (chatBanned && !isAdmin) {
      toast.error("You're banned from chat. Contact ShoutOut support.");
      return;
    }
    setSending(true);
    const msgData: any = {
      channel_id: activeChannel!.id,
      content: text,
      message_type: 'text',
    };
    if (isAdmin) msgData.sender_user_id = adminUserId;
    else msgData.sender_talent_id = talentId;
    const threadParentId = replyingTo ? resolveThreadParentId(replyingTo) : null;
    if (threadParentId) msgData.parent_message_id = threadParentId;

    const { data: inserted, error } = await supabase.from('chat_messages').insert(msgData).select().single();
    if (error) {
      // The DB trigger raises 'chat_banned' for banned senders. Translate
      // that into a user-friendly toast and lock the UI in sync with the
      // server's view of the world.
      if (String(error.message || '').toLowerCase().includes('chat_banned')) {
        setChatBanned(true);
        toast.error("You're banned from chat. Contact ShoutOut support.");
      } else {
        toast.error('Failed to send');
      }
      setSending(false);
      return;
    }
    triggerPushNotification(activeChannel!.id, talentName, text, threadParentId);
    setMessageInput('');
    if (inputRef.current) inputRef.current.style.height = 'auto';
    if (inserted && threadParentId) {
      setThreadReplies(prev => ({ ...prev, [threadParentId]: [...(prev[threadParentId] || []), inserted] }));
      resolveSender(inserted);
      setMessages(prev => prev.map(m => m.id === threadParentId ? { ...m, reply_count: (m.reply_count || 0) + 1 } : m));
      // Make sure the thread is expanded so the user sees their new reply.
      // We DO NOT call openThread() here because it re-fetches and, critically,
      // re-sets replyingTo to the parent id (undoing the reset below).
      setExpandedThreads(prev => prev.has(threadParentId) ? prev : new Set(prev).add(threadParentId));
    } else if (inserted) {
      // Optimistically append the just-sent top-level message so the sender
      // sees it instantly. The realtime INSERT subscription de-dupes by id so
      // this is safe even when the websocket echoes the row back a moment later.
      resolveSender(inserted);
      setMessages(prev => prev.some(m => m.id === inserted.id) ? prev : [...prev, { ...inserted, reply_count: 0 }]);
    }
    setReplyingTo(null);
    setSending(false);
    scrollToBottom();
  };

  // Send GIF (uses replyingTo if active)
  const sendGif = async (gif: GiphyGif) => {
    if (chatBanned && !isAdmin) {
      toast.error("You're banned from chat. Contact ShoutOut support.");
      return;
    }
    const msgData: any = {
      channel_id: activeChannel!.id,
      content: gif.title || 'GIF',
      message_type: 'gif',
      gif_url: gif.images.original.url,
    };
    if (isAdmin) msgData.sender_user_id = adminUserId;
    else msgData.sender_talent_id = talentId;
    const gifThreadParent = replyingTo ? resolveThreadParentId(replyingTo) : null;
    if (gifThreadParent) msgData.parent_message_id = gifThreadParent;

    const { data: inserted, error: gifError } = await supabase.from('chat_messages').insert(msgData).select().single();
    if (gifError && String(gifError.message || '').toLowerCase().includes('chat_banned')) {
      setChatBanned(true);
      toast.error("You're banned from chat. Contact ShoutOut support.");
      return;
    }
    if (inserted) {
      resolveSender(inserted);
      if (gifThreadParent) {
        setThreadReplies(prev => ({ ...prev, [gifThreadParent]: [...(prev[gifThreadParent] || []), inserted] }));
        setMessages(prev => prev.map(m => m.id === gifThreadParent ? { ...m, reply_count: (m.reply_count || 0) + 1 } : m));
        setExpandedThreads(prev => prev.has(gifThreadParent) ? prev : new Set(prev).add(gifThreadParent));
      } else {
        setMessages(prev => prev.some(m => m.id === inserted.id) ? prev : [...prev, { ...inserted, reply_count: 0 }]);
        scrollToBottom();
      }
    }
    setShowGifPicker(false);
    setGifSearch('');
    setGifResults([]);
    setReplyingTo(null);
  };

  const deleteMessage = async (messageId: string, parentId?: string | null) => {
    await supabase.from('chat_messages').update({ is_deleted: true }).eq('id', messageId);
    if (parentId) {
      setThreadReplies(prev => ({ ...prev, [parentId]: (prev[parentId] || []).filter(r => r.id !== messageId) }));
      setMessages(prev => prev.map(m => m.id === parentId ? { ...m, reply_count: Math.max(0, (m.reply_count || 1) - 1) } : m));
    } else {
      setMessages(prev => prev.filter(m => m.id !== messageId));
    }
  };

  const canDeleteMessage = (msg: Message) => {
    return msg.sender_talent_id === talentId || (isAdmin && msg.sender_user_id === adminUserId);
  };

  // Toggle reaction
  const toggleReaction = async (messageId: string, emoji: string) => {
    const existing = reactions[messageId]?.find(r => r.emoji === emoji && r.reacted);
    if (existing) {
      const filter = isAdmin
        ? supabase.from('chat_reactions').delete().eq('message_id', messageId).eq('emoji', emoji).eq('user_id', adminUserId)
        : supabase.from('chat_reactions').delete().eq('message_id', messageId).eq('emoji', emoji).eq('talent_id', talentId);
      await filter;
    } else {
      const data: any = { message_id: messageId, emoji };
      if (isAdmin) data.user_id = adminUserId;
      else data.talent_id = talentId;
      await supabase.from('chat_reactions').insert(data);
    }
    // Optimistic update
    setReactions(prev => {
      const msgReactions = [...(prev[messageId] || [])];
      const idx = msgReactions.findIndex(r => r.emoji === emoji);
      if (existing && idx >= 0) {
        msgReactions[idx] = { ...msgReactions[idx], count: msgReactions[idx].count - 1, reacted: false };
        if (msgReactions[idx].count <= 0) msgReactions.splice(idx, 1);
      } else if (idx >= 0) {
        msgReactions[idx] = { ...msgReactions[idx], count: msgReactions[idx].count + 1, reacted: true };
      } else {
        msgReactions.push({ emoji, count: 1, reacted: true });
      }
      return { ...prev, [messageId]: msgReactions };
    });
    setShowReactionPicker(null);
  };

  // Search GIFs
  useEffect(() => {
    if (!showGifPicker) return;
    const timeout = setTimeout(async () => {
      setGifLoading(true);
      const endpoint = gifSearch
        ? `https://api.giphy.com/v1/gifs/search?api_key=${GIPHY_API_KEY}&q=${encodeURIComponent(gifSearch)}&limit=20&rating=pg-13`
        : `https://api.giphy.com/v1/gifs/trending?api_key=${GIPHY_API_KEY}&limit=20&rating=pg-13`;
      try {
        const res = await fetch(endpoint);
        const json = await res.json();
        setGifResults(json.data || []);
      } catch { setGifResults([]); }
      setGifLoading(false);
    }, gifSearch ? 400 : 0);
    return () => clearTimeout(timeout);
  }, [gifSearch, showGifPicker]);

  // Long-press handlers for mobile.
  // NOTE: we must NOT call e.preventDefault() here. Doing so on touchstart
  // suppresses the synthetic click for the whole gesture, which kills taps on
  // the per-message action buttons (Reply, reactions, "view replies"). That's
  // why replying stopped working on phones. Long-press text highlighting is
  // already prevented by the row's CSS (select-none / WebkitUserSelect:none /
  // WebkitTouchCallout:none), so preventDefault is unnecessary anyway.
  const handleTouchStart = useCallback((_e: React.TouchEvent, msgId: string) => {
    longPressTimer.current = setTimeout(() => {
      window.getSelection()?.removeAllRanges();
      setLongPressMsg(msgId);
    }, 500);
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  // @mention / #channel handler
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setMessageInput(val);
    const cursorPos = e.target.selectionStart || val.length;
    const textUpToCursor = val.slice(0, cursorPos);
    const mentionMatch = textUpToCursor.match(/@(\w*)$/);
    const channelMatch = textUpToCursor.match(/#(\w*)$/);

    if (mentionMatch) {
      const query = mentionMatch[1].toLowerCase();
      setMentionSearch(query);
      const userResults = channelTalentList.filter(t => t.name.toLowerCase().includes(query)).slice(0, 6);
      if (canMentionEveryone && 'everyone'.includes(query)) {
        setMentionResults([{ id: '__everyone__', name: 'everyone' }, ...userResults]);
      } else {
        setMentionResults(userResults);
      }
      setChannelMentionSearch(null);
      setChannelMentionResults([]);
    } else if (channelMatch && isAdmin) {
      // #channel autocomplete is admin-only — admins drop clickable channel
      // tags into messages so creators can jump to that channel.
      const query = channelMatch[1].toLowerCase();
      setChannelMentionSearch(query);
      const chResults = channels
        .filter(c => c.channel_type !== 'support' && c.name.toLowerCase().includes(query))
        .slice(0, 8)
        .map(c => ({ id: c.id, name: c.name, channel_type: c.channel_type }));
      setChannelMentionResults(chResults);
      setMentionSearch(null);
      setMentionResults([]);
    } else {
      setMentionSearch(null);
      setMentionResults([]);
      setChannelMentionSearch(null);
      setChannelMentionResults([]);
    }
  }, [channelTalentList, channels, isAdmin, canMentionEveryone]);

  const insertMention = useCallback((name: string) => {
    const cursorPos = inputRef.current?.selectionStart || messageInput.length;
    const textUpToCursor = messageInput.slice(0, cursorPos);
    const mentionMatch = textUpToCursor.match(/@(\w*)$/);
    if (mentionMatch) {
      const before = textUpToCursor.slice(0, mentionMatch.index);
      const after = messageInput.slice(cursorPos);
      const newVal = `${before}@${name.replace(/\s/g, '')} ${after}`;
      setMessageInput(newVal);
    }
    setMentionSearch(null);
    setMentionResults([]);
    inputRef.current?.focus();
  }, [messageInput]);

  const insertChannelMention = useCallback((name: string) => {
    const cursorPos = inputRef.current?.selectionStart || messageInput.length;
    const textUpToCursor = messageInput.slice(0, cursorPos);
    const channelMatch = textUpToCursor.match(/#(\w*)$/);
    if (channelMatch) {
      const before = textUpToCursor.slice(0, channelMatch.index);
      const after = messageInput.slice(cursorPos);
      setMessageInput(`${before}#${name.replace(/\s/g, '')} ${after}`);
    }
    setChannelMentionSearch(null);
    setChannelMentionResults([]);
    inputRef.current?.focus();
  }, [messageInput]);

  const openProfileCard = useCallback(async (tId: string) => {
    const [{ data: tp }, { data: socials }] = await Promise.all([
      supabase.from('talent_profiles').select('full_name, temp_full_name, temp_avatar_url, bio, slug').eq('id', tId).single(),
      supabase.from('social_accounts').select('platform, handle').eq('talent_id', tId),
    ]);
    if (tp) {
      setProfileCard({
        talentId: tId,
        name: tp.full_name || tp.temp_full_name || 'Creator',
        avatar: tp.temp_avatar_url || undefined,
        bio: tp.bio || undefined,
        slug: tp.slug || undefined,
        socials: socials || [],
      });
    }
  }, []);

  const formatTime = (ts: string) => {
    const d = new Date(ts);
    const now = new Date();
    const diff = now.getTime() - d.getTime();
    if (diff < 60000) return 'just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ' ' + d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  };

  const getSenderInfo = (msg: Message) => {
    const key = msg.sender_talent_id || msg.sender_user_id || 'unknown';
    return senderNames[key] || { name: 'Unknown', role: 'member' };
  };

  const stripFormatting = (text: string) =>
    text.replace(/\*\*([^*]+)\*\*/g, '$1').replace(/\*([^*]+)\*/g, '$1');

  const isTrustedDesignDeepLink = (href: string) => {
    try {
      const url = new URL(href);
      const tabValues = url.searchParams.getAll('tab');
      const requestedTab = tabValues[0];

      return url.origin === 'https://creator.shoutout.us'
        && !url.username
        && !url.password
        && (url.pathname === '/dashboard' || url.pathname === '/dashboard/')
        && tabValues.length === 1
        && (requestedTab === 'design' || requestedTab === 'customize')
        && !url.searchParams.has('view')
        && !url.searchParams.has('token')
        && !url.searchParams.has('email')
        && !url.searchParams.has('userId');
    } catch {
      return false;
    }
  };

  const linkifyText = (text: string, keyPrefix: string): React.ReactNode[] => {
    const urlRegex = /https?:\/\/[^\s]+|www\.[^\s]+/g;
    const result: React.ReactNode[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    let matchIndex = 0;

    while ((match = urlRegex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        result.push(<span key={`${keyPrefix}-t${matchIndex}`}>{text.slice(lastIndex, match.index)}</span>);
      }
      const url = match[0];
      const href = url.startsWith('http') ? url : `https://${url}`;
      const navigateInApp = Boolean(onNavigateToDesign) && isTrustedDesignDeepLink(href);
      result.push(
        <a key={`${keyPrefix}-l${matchIndex}`} href={href}
          target={navigateInApp ? undefined : '_blank'}
          rel={navigateInApp ? undefined : 'noopener noreferrer'}
          className="text-blue-400 hover:text-blue-300 hover:underline break-all"
          onClick={(e) => {
            e.stopPropagation();
            if (!navigateInApp) return;
            e.preventDefault();
            closeChat();
            onNavigateToDesign?.();
          }}>
          {url}
        </a>
      );
      lastIndex = urlRegex.lastIndex;
      matchIndex++;
    }
    if (lastIndex < text.length) {
      result.push(<span key={`${keyPrefix}-t-end`}>{text.slice(lastIndex)}</span>);
    }
    return result;
  };

  const renderFormattedText = (text: string) => {
    const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*|@\w+|#\w+)/g);
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) return <strong key={i} className="font-bold">{part.slice(2, -2)}</strong>;
      if (part.startsWith('*') && part.endsWith('*')) return <em key={i} className="italic">{part.slice(1, -1)}</em>;
      if (part === '@everyone') return <span key={i} className="text-yellow-300 font-semibold bg-yellow-500/15 px-0.5 rounded">{part}</span>;
      if (part.startsWith('@')) return <span key={i} className="text-blue-400 font-semibold bg-blue-500/10 px-0.5 rounded">{part}</span>;
      if (part.startsWith('#')) {
        const chName = part.slice(1).toLowerCase();
        const ch = channels.find(c => c.name.toLowerCase() === chName);
        if (ch) return <button key={i} onClick={() => { setActiveChannel(ch); setShowMobileSidebar(false); }} className="text-blue-400 font-semibold bg-blue-500/10 px-0.5 rounded hover:underline">{part}</button>;
        return <span key={i} className="text-blue-400 font-semibold bg-blue-500/10 px-0.5 rounded">{part}</span>;
      }
      return <React.Fragment key={i}>{linkifyText(part, `ft-${i}`)}</React.Fragment>;
    });
  };

  const renderMentions = (text: string) => {
    const parts = text.split(/(@\w+|#\w+)/g);
    return parts.map((part, i) => {
      if (part === '@everyone') return <span key={i} className="text-yellow-300 font-semibold bg-yellow-500/15 px-0.5 rounded">{part}</span>;
      if (part.startsWith('@')) return <span key={i} className="text-blue-400 font-semibold bg-blue-500/10 px-0.5 rounded">{part}</span>;
      if (part.startsWith('#')) {
        const chName = part.slice(1).toLowerCase();
        const ch = channels.find(c => c.name.toLowerCase() === chName);
        if (ch) return <button key={i} onClick={() => { setActiveChannel(ch); setShowMobileSidebar(false); }} className="text-blue-400 font-semibold bg-blue-500/10 px-0.5 rounded hover:underline">{part}</button>;
        return <span key={i} className="text-blue-400 font-semibold bg-blue-500/10 px-0.5 rounded">{part}</span>;
      }
      return <React.Fragment key={i}>{linkifyText(part, `mn-${i}`)}</React.Fragment>;
    });
  };

  const renderMessage = (msg: Message, isThread = false) => {
    const sender = getSenderInfo(msg);
    const isAnnouncements = activeChannel?.channel_type === 'announcements';
    const showAmaFeatureDemo = isAnnouncements
      && !isThread
      && msg.id === AMA_LAUNCH_ANNOUNCEMENT_ID;
    const msgReactions = reactions[msg.id] || [];
    const isMe = isAdmin ? msg.sender_user_id === adminUserId : msg.sender_talent_id === talentId;

    // One grid, shared by the desktop hover picker and the mobile long-press
    // sheet, so the two surfaces can never drift apart. EMOJI_LIST is the whole
    // palette — there is deliberately no "more emoji" escape hatch.
    const emojiGrid = (
      <div className="grid grid-cols-8 gap-0.5">
        {EMOJI_LIST.map(emoji => {
          const mine = msgReactions.find(r => r.emoji === emoji)?.reacted;
          return (
            <button
              key={emoji}
              onClick={() => {
                toggleReaction(msg.id, emoji);
                setShowReactionPicker(null);
                setLongPressMsg(null);
              }}
              className={`text-xl leading-none p-1.5 rounded-lg transition-transform hover:scale-125 hover:bg-white/10 ${
                mine ? 'bg-blue-500/25' : ''
              }`}
              aria-label={`React with ${emoji}`}
            >
              {emoji}
            </button>
          );
        })}
      </div>
    );

    return (
      <div
        key={msg.id}
        className={`group px-4 py-2 hover:bg-white/5 transition-colors relative select-none animate-[msgIn_0.25s_ease-out]`}
        style={{ WebkitTouchCallout: 'none', WebkitUserSelect: 'none', animation: 'msgIn 0.2s ease-out' }}
        onTouchStart={(e) => handleTouchStart(e, msg.id)}
        onTouchEnd={handleTouchEnd}
        onTouchMove={handleTouchEnd}
      >
        {/* MOBILE: press-and-hold opens the reaction palette. Nothing is visible
            on the message until then. Reply/Delete ride along underneath because
            long-press is their only entry point on touch — the desktop hover
            cluster does not exist here. */}
        {longPressMsg === msg.id && (
          <div className="absolute inset-0 z-30 sm:hidden">
            <div className="fixed inset-0" onClick={() => setLongPressMsg(null)} />
            <div className="absolute left-2 right-2 top-0 z-40 bg-gray-900 border border-white/20 rounded-2xl shadow-2xl p-2 animate-fade-in">
              {emojiGrid}
              {(!isAnnouncements || canDeleteMessage(msg)) && (
                <div className="mt-1.5 pt-1.5 border-t border-white/10 flex items-center gap-1">
                  {!isAnnouncements && (
                    <button
                      onClick={() => { setReplyingTo(msg.id); setLongPressMsg(null); inputRef.current?.focus(); }}
                      className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm text-gray-300 hover:bg-white/10"
                    >
                      <ArrowUturnLeftIcon className="h-4 w-4" /> Reply
                    </button>
                  )}
                  {canDeleteMessage(msg) && (
                    <button
                      onClick={() => { deleteMessage(msg.id); setLongPressMsg(null); }}
                      className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm text-red-400 hover:bg-red-500/20"
                    >
                      <TrashIcon className="h-4 w-4" /> Delete
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="flex items-start gap-3">
          {sender.role === 'jai' ? (
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-fuchsia-600 flex items-center justify-center flex-shrink-0 mt-0.5" title="JAI — ShoutOut AI support">
              <span className="text-white text-[10px] font-bold">JAI</span>
            </div>
          ) : sender.role === 'admin' ? (
            <div className="w-8 h-8 rounded-full bg-black border border-white/20 flex items-center justify-center flex-shrink-0 mt-0.5 overflow-hidden">
              <img src={ADMIN_ICON_URL} alt="Admin" className="w-5 h-5 object-contain" />
            </div>
          ) : (
            <ResilientAvatar
              src={sender.avatar !== ADMIN_ICON_URL ? sender.avatar : undefined}
              name={sender.name}
              imgClass={`w-8 h-8 rounded-full object-cover flex-shrink-0 mt-0.5 ${msg.sender_talent_id ? 'cursor-pointer hover:ring-2 hover:ring-blue-500 transition-all' : ''}`}
              fallbackClass={`w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0 mt-0.5 ${msg.sender_talent_id ? 'cursor-pointer hover:ring-2 hover:ring-blue-500 transition-all' : ''}`}
              onClick={msg.sender_talent_id ? () => openProfileCard(msg.sender_talent_id!) : undefined}
            />
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-2">
              <span className={`text-sm font-semibold ${
                sender.role === 'jai' ? 'text-purple-300' :
                sender.role === 'admin' ? 'text-red-400' :
                sender.role === 'lead' ? 'text-amber-300' :
                sender.role === 'core_team' ? 'text-emerald-300' :
                'text-white'
              } ${msg.sender_talent_id ? 'cursor-pointer hover:underline' : ''}`}
                onClick={() => msg.sender_talent_id && openProfileCard(msg.sender_talent_id)}>
                {sender.name}
              </span>
              {sender.role === 'jai' && (
                <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-purple-500/20 text-purple-300 uppercase">AI · Admin</span>
              )}
              {sender.role === 'admin' && (
                <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-red-500/20 text-red-400 uppercase">Admin</span>
              )}
              {sender.role === 'lead' && (
                <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-amber-500/20 text-amber-300 uppercase">Lead</span>
              )}
              {sender.role === 'core_team' && (
                <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-emerald-500/20 text-emerald-300 uppercase">Core Team</span>
              )}
              <span className="text-[11px] text-gray-500">{formatTime(msg.created_at)}</span>
            </div>
            {/* Content and the desktop hover cluster share an inline-flex row so
                the controls sit immediately beside the message rather than being
                flung to the far edge of a wide panel. The wrapper shrinks to the
                content's width, so on a short message the icons land right next
                to the last word. */}
            <div className="inline-flex items-start gap-1.5 max-w-full align-top">
              <div className="min-w-0">
                {msg.message_type === 'gif' && msg.gif_url ? (
                  <img src={msg.gif_url} alt={msg.content} className="max-w-[250px] rounded-lg mt-1" />
                ) : msg.message_type === 'image' && msg.image_url ? (
                  <a href={msg.image_url} target="_blank" rel="noopener noreferrer">
                    <img src={msg.image_url} alt={msg.content} className="max-w-[250px] max-h-[300px] object-cover rounded-lg mt-1 cursor-pointer hover:opacity-90" />
                  </a>
                ) : (
                  <p className="text-sm text-gray-200 whitespace-pre-wrap break-words mt-0.5">{activeChannel?.channel_type === 'announcements' ? renderFormattedText(msg.content) : renderMentions(msg.content)}</p>
                )}
              </div>

              {/* DESKTOP: hover reveals react / reply / delete. Hidden on touch,
                  where long-press is the entry point instead. Stays visible while
                  its own picker is open, or it would vanish the moment the cursor
                  left the message row. */}
              <div className={`relative hidden sm:flex flex-none items-center gap-1 transition-opacity ${
                showReactionPicker === msg.id
                  ? 'opacity-100'
                  : 'opacity-0 group-hover:opacity-100 focus-within:opacity-100'
              }`}>
                <button
                  onClick={() => setShowReactionPicker(showReactionPicker === msg.id ? null : msg.id)}
                  title="Add reaction"
                  aria-label="Add reaction"
                  className="p-1 rounded-full bg-gray-800 border border-white/10 hover:bg-white/10 text-gray-400 hover:text-white shadow-lg transition-colors"
                >
                  <FaceSmileIcon className="h-4 w-4" />
                </button>
                {!isAnnouncements && (
                  <button
                    onClick={() => { setReplyingTo(msg.id); inputRef.current?.focus(); }}
                    title="Reply"
                    aria-label="Reply"
                    className="p-1 rounded-full bg-gray-800 border border-white/10 hover:bg-white/10 text-gray-400 hover:text-white shadow-lg transition-colors"
                  >
                    <ArrowUturnLeftIcon className="h-4 w-4" />
                  </button>
                )}
                {canDeleteMessage(msg) && (
                  <button
                    onClick={() => deleteMessage(msg.id)}
                    title="Delete"
                    aria-label="Delete message"
                    className="p-1 rounded-full bg-gray-800 border border-white/10 hover:bg-red-500/20 hover:border-red-500/30 text-gray-400 hover:text-red-400 shadow-lg transition-colors"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                )}

                {/* Picker hangs off the cluster, so it opens under the smiley
                    that summoned it however wide the panel is.
                    `w-max` is load-bearing: an absolutely positioned box with
                    only `left` set is shrink-to-fit, and the containing block
                    here is the ~100px icon cluster — without it the eight
                    columns get crushed into that width. */}
                {showReactionPicker === msg.id && (
                  <div className="absolute left-0 top-8 z-40 w-max p-2 bg-gray-900 border border-white/15 rounded-2xl shadow-2xl animate-fade-in">
                    {emojiGrid}
                  </div>
                )}
              </div>
            </div>
            {showAmaFeatureDemo && (
              <AmaFeatureDemo className="ama-announcement-demo" />
            )}
            {/* Reactions that actually exist. No placeholder buttons — a message
                nobody has reacted to shows nothing at all. */}
            {msgReactions.some(r => r.count > 0) && (
              <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                {msgReactions.filter(r => r.count > 0).map(r => (
                  <button
                    key={r.emoji}
                    onClick={() => toggleReaction(msg.id, r.emoji)}
                    title={r.reacted ? 'Remove your reaction' : 'React'}
                    className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-sm transition-colors active:scale-95 ${
                      r.reacted
                        ? 'bg-blue-500/20 border border-blue-500/40'
                        : 'bg-white/5 border border-white/10 hover:bg-white/10'
                    }`}
                  >
                    <span className="text-base leading-none">{r.emoji}</span>
                    <span className="text-gray-400 text-xs">{r.count}</span>
                  </button>
                ))}
              </div>
            )}
            {/* Thread / Reply info */}
            {!isThread && !isAnnouncements && (
              <>
                {msg.reply_count && msg.reply_count > 0 ? (() => {
                  const allReplies = threadReplies[msg.id] || [];
                  const isExpanded = expandedThreads.has(msg.id);
                  const visibleReplies = isExpanded ? allReplies : allReplies.slice(-PREVIEW_REPLY_COUNT);
                  // While collapsed, `allReplies` only holds the preview fetch's
                  // last two, so the hidden count HAS to come from the message's
                  // own reply_count — deriving it from the array made a
                  // 20-reply thread report 0 hidden and hid the only way in.
                  const totalReplies = Math.max(msg.reply_count || 0, allReplies.length);
                  const hiddenCount = Math.max(0, totalReplies - PREVIEW_REPLY_COUNT);
                  return (
                    <div className="mt-2 ml-2 pl-3 border-l-2 border-blue-500/30 space-y-2">
                      {!isExpanded && hiddenCount > 0 && (
                        <button onClick={() => openThread(msg)} className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1">
                          <ArrowUturnLeftIcon className="h-3 w-3" />
                          View {hiddenCount} more {hiddenCount === 1 ? 'reply' : 'replies'}
                        </button>
                      )}
                      {visibleReplies.length === 0 && !isExpanded ? (
                        <button onClick={() => openThread(msg)} className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1">
                          <ArrowUturnLeftIcon className="h-3 w-3" />
                          {msg.reply_count} {msg.reply_count === 1 ? 'reply' : 'replies'}
                        </button>
                      ) : (
                        visibleReplies.map(reply => {
                          const replySender = getSenderInfo(reply);
                          return (
                            <div key={reply.id} className="flex items-start gap-2 animate-[fadeSlideIn_0.2s_ease-out]">
                              {replySender.role === 'admin' ? (
                                <div className="w-6 h-6 rounded-full bg-black border border-white/20 flex items-center justify-center flex-shrink-0 overflow-hidden">
                                  <img src={ADMIN_ICON_URL} alt="Admin" className="w-4 h-4 object-contain" />
                                </div>
                              ) : (
                                <ResilientAvatar
                                  src={replySender.avatar !== ADMIN_ICON_URL ? replySender.avatar : undefined}
                                  name={replySender.name}
                                  imgClass="w-6 h-6 rounded-full object-cover flex-shrink-0"
                                  fallbackClass="w-6 h-6 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-[9px] font-bold flex-shrink-0"
                                />
                              )}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-baseline gap-1.5">
                                  <span className={`text-xs font-semibold ${
                                    replySender.role === 'admin' ? 'text-red-400' :
                                    replySender.role === 'lead' ? 'text-amber-300' :
                                    'text-white'}`}>{replySender.name}</span>
                                  {replySender.role === 'lead' && (
                                    <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-amber-500/20 text-amber-300 uppercase">Lead</span>
                                  )}
                                  {replySender.role === 'jai' && (
                                    <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-purple-500/20 text-purple-300 uppercase">AI · Admin</span>
                                  )}
                                  {replySender.role === 'admin' && (
                                    <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-red-500/20 text-red-400 uppercase">Admin</span>
                                  )}
                                  <span className="text-[10px] text-gray-500">{formatTime(reply.created_at)}</span>
                                </div>
                                {reply.message_type === 'gif' && reply.gif_url ? (
                                  <img src={reply.gif_url} alt={reply.content} className="max-w-[180px] rounded-lg mt-0.5" />
                                ) : reply.message_type === 'image' && reply.image_url ? (
                                  <a href={reply.image_url} target="_blank" rel="noopener noreferrer">
                                    <img src={reply.image_url} alt={reply.content} className="max-w-[180px] max-h-[200px] object-cover rounded-lg mt-0.5 cursor-pointer hover:opacity-90" />
                                  </a>
                                ) : (
                                  <p className="text-xs text-gray-300 whitespace-pre-wrap break-words">{renderMentions(reply.content)}</p>
                                )}
                              </div>
                              <button onClick={() => { setReplyingTo(msg.id); inputRef.current?.focus(); }}
                                className="p-1 rounded text-gray-500 hover:text-blue-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                                <ArrowUturnLeftIcon className="h-4 w-4" />
                              </button>
                              {canDeleteMessage(reply) && (
                                <button onClick={() => deleteMessage(reply.id, msg.id)}
                                  className="p-0.5 rounded text-gray-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                                  <TrashIcon className="h-3 w-3" />
                                </button>
                              )}
                            </div>
                          );
                        })
                      )}
                      {isExpanded && hiddenCount > 0 && (
                        <button onClick={() => { setExpandedThreads(prev => { const s = new Set(prev); s.delete(msg.id); return s; }); }}
                          className="text-xs text-gray-500 hover:text-gray-300">
                          Show less
                        </button>
                      )}
                    </div>
                  );
                })() : null}
              </>
            )}
          </div>
        </div>
        {/* Click-away for the desktop picker. The picker itself lives inside the
            hover cluster so it tracks the message, not the panel edge. */}
        {showReactionPicker === msg.id && (
          <div className="hidden sm:block fixed inset-0 z-30" onClick={() => setShowReactionPicker(null)} />
        )}
      </div>
    );
  };

  const handleSwipeStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  }, []);

  const handleSwipeEnd = useCallback((e: React.TouchEvent) => {
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    const dy = Math.abs(e.changedTouches[0].clientY - touchStartY.current);
    if (dx > 80 && dy < 80 && !showMobileSidebar) {
      setShowMobileSidebar(true);
    } else if (dx < -80 && dy < 80 && showMobileSidebar) {
      setShowMobileSidebar(false);
    }
  }, [showMobileSidebar]);

  const shortTime = (ts: string) => {
    const d = new Date(ts);
    const now = new Date();
    const diff = now.getTime() - d.getTime();
    if (diff < 60000) return 'now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h`;
    return `${Math.floor(diff / 86400000)}d`;
  };

  // Announcement preview bubble
  if (!isOpen && showAnnouncementPreview && lastAnnouncement) {
    return (
      <>
        <div
          className="fixed bottom-24 left-6 z-50 max-w-[220px] bg-gray-900/95 border border-white/10 rounded-xl px-3 py-2 shadow-xl cursor-pointer animate-fade-in backdrop-blur-sm"
          onClick={() => { setIsOpen(true); setShowMobileSidebar(true); setActiveChannel(null); setShowAnnouncementPreview(false); }}
        >
          <div className="flex items-center gap-1.5">
            <MegaphoneIcon className="h-3 w-3 text-yellow-400 flex-shrink-0" />
            <p className="text-xs text-gray-300 line-clamp-1 flex-1 min-w-0">{stripFormatting(lastAnnouncement.content)}</p>
            <button onClick={(e) => { e.stopPropagation(); setShowAnnouncementPreview(false); }} className="text-gray-500 hover:text-white flex-shrink-0">
              <XMarkIcon className="h-3 w-3" />
            </button>
          </div>
        </div>
        <div className="fixed bottom-6 left-6 z-50" style={{ filter: 'drop-shadow(0 12px 40px rgba(0,0,0,0.95)) drop-shadow(0 6px 20px rgba(0,0,0,0.8)) drop-shadow(0 2px 10px rgba(80,0,120,0.5))' }}>
          <button
            onClick={() => { setIsOpen(true); setShowMobileSidebar(true); setActiveChannel(null); setShowAnnouncementPreview(false); }}
            className="relative w-16 h-16 rounded-full p-[2.5px] text-white flex items-center justify-center hover:scale-105 transition-transform"
            style={{ background: 'linear-gradient(135deg, #833ab4, #c13584, #fd1d1d, #fcb045)' }}
          >
            <div className="w-full h-full rounded-full bg-[#2d1854] flex items-center justify-center">
              <ChatBubbleLeftRightIcon className="h-8 w-8" />
            </div>
            {unreadMentions > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[22px] h-[22px] px-1 flex items-center justify-center text-xs font-bold rounded-full bg-red-500 text-white shadow-lg">
                {unreadMentions > 99 ? '99+' : unreadMentions}
              </span>
            )}
          </button>
        </div>
      </>
    );
  }

  // Chat bubble (closed state)
  if (!isOpen) {
    return (
      <div className="fixed bottom-6 left-6 z-50" style={{ filter: 'drop-shadow(0 12px 40px rgba(0,0,0,0.95)) drop-shadow(0 6px 20px rgba(0,0,0,0.8)) drop-shadow(0 2px 10px rgba(80,0,120,0.5))' }}>
        <button
          onClick={() => { setIsOpen(true); setShowMobileSidebar(true); setActiveChannel(null); }}
          className="relative w-16 h-16 rounded-full p-[2.5px] text-white flex items-center justify-center hover:scale-105 transition-transform"
          style={{ background: 'linear-gradient(135deg, #833ab4, #c13584, #fd1d1d, #fcb045)' }}
        >
          <div className="w-full h-full rounded-full bg-[#2d1854] flex items-center justify-center">
            <ChatBubbleLeftRightIcon className="h-8 w-8" />
          </div>
          {unreadMentions > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[22px] h-[22px] px-1 flex items-center justify-center text-xs font-bold rounded-full bg-red-500 text-white shadow-lg">
              {unreadMentions > 99 ? '99+' : unreadMentions}
            </span>
          )}
        </button>
      </div>
    );
  }

  // Full chat interface (open state)
  return createPortal(
    <div
      className="fixed inset-0 z-[100] isolate overflow-hidden bg-[#0a0a0a] flex flex-col"
      style={{ height: '100dvh', maxHeight: '100dvh' }}
      onTouchStart={handleSwipeStart}
      onTouchEnd={handleSwipeEnd}
      role="dialog"
      aria-modal="true"
      aria-label="Creator chat"
    >
      <style>{chatAnimationStyles}</style>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-black/60 border-b border-white/10 backdrop-blur-xl" style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 12px)' }}>
        <div className="flex items-center gap-3">
          {activeChannel && !showMobileSidebar ? (
            <>
              <button onClick={() => setShowMobileSidebar(true)} className="lg:hidden p-3 -ml-2 text-gray-400 hover:text-white active:bg-white/10 rounded-lg">
                <ChevronLeftIcon className="h-6 w-6" />
              </button>
              {activeChannel.channel_type === 'announcements' ? <MegaphoneIcon className="h-5 w-5 text-yellow-400" /> : activeChannel.channel_type === 'support' ? <ChatBubbleLeftRightIcon className="h-5 w-5 text-green-400" /> : activeChannel.channel_type === 'dm' ? <span className="text-base">🤝</span> : <HashtagIcon className="h-5 w-5 text-blue-400" />}
              <span className="text-white font-semibold text-sm">{activeChannel.name}</span>
              {channelMemberCount > 0 && (
                <div className="flex items-center gap-1.5 ml-1">
                  <div className="flex -space-x-2">
                    {channelMemberAvatars.slice(0, 3).map((m, i) => (
                      <ResilientAvatar key={i}
                        src={m.avatar}
                        name={m.name}
                        imgClass="w-5 h-5 rounded-full border-2 border-[#0a0a0a] object-cover"
                        fallbackClass="w-5 h-5 rounded-full border-2 border-[#0a0a0a] bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-[8px] font-bold"
                      />
                    ))}
                  </div>
                  <span className="text-gray-500 text-xs">{channelMemberCount}</span>
                </div>
              )}
            </>
          ) : (
            <>
              <ChatBubbleLeftRightIcon className="h-5 w-5 text-blue-400" />
              <span className="text-white font-semibold text-sm">Channels</span>
            </>
          )}
        </div>
        <button onClick={closeChat} className="p-3 -mr-1 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg active:bg-white/20">
          <XMarkIcon className="h-6 w-6" />
        </button>
      </div>

      <div className="flex min-h-0 flex-1 overflow-hidden">
        {/* Channels sidebar / landing */}
        <div className={`${showMobileSidebar ? 'flex' : 'hidden'} lg:flex flex-col w-full lg:w-64 bg-black/40 border-r border-white/10 flex-shrink-0`}>
          <div className="px-4 pt-4 pb-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">Channels</div>
          <div className="flex-1 overflow-y-auto px-3 space-y-1">
            {channels.filter(ch => ch.channel_type !== 'support' && ch.channel_type !== 'dm').map(ch => {
              const badge = channelBadges[ch.id] || 0;
              const preview = channelPreviews[ch.id];
              return (
                <button
                  key={ch.id}
                  onClick={() => { setActiveChannel(ch); setShowMobileSidebar(false); setExpandedThreads(new Set()); setReplyingTo(null); }}
                  className={`w-full flex items-start gap-3.5 px-4 py-4 rounded-2xl text-left transition-colors active:bg-white/15 ${
                    activeChannel?.id === ch.id ? 'bg-white/10' : 'hover:bg-white/5'
                  }`}
                >
                  <div className="flex-shrink-0 mt-0.5 w-10 h-10 rounded-full bg-white/5 flex items-center justify-center">
                    {ch.channel_type === 'announcements' ? <MegaphoneIcon className="h-5 w-5 text-yellow-400" /> : ch.channel_type === 'dm' ? <span className="text-base">🤝</span> : <HashtagIcon className="h-5 w-5 text-blue-400" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className={`text-[15px] font-semibold truncate ${badge > 0 ? 'text-white' : 'text-gray-200'}`}>{ch.name}</span>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {preview && <span className="text-xs text-gray-500">{shortTime(preview.created_at)}</span>}
                        {badge > 0 && (
                          <span className="min-w-[20px] h-[20px] px-1.5 flex items-center justify-center text-[11px] font-bold rounded-full bg-red-500 text-white">
                            {badge > 99 ? '99+' : badge}
                          </span>
                        )}
                      </div>
                    </div>
                    {preview && (
                      <p className={`text-[13px] truncate mt-1 ${badge > 0 ? 'text-gray-300' : 'text-gray-500'}`}>
                        <span className="font-medium">{preview.sender}:</span> {stripFormatting(preview.content)}
                      </p>
                    )}
                  </div>
                </button>
              );
            })}

            {/* Network (DM) section */}
            {channels.some(ch => ch.channel_type === 'dm') && (
              <>
                <div className="pt-3 pb-1">
                  <div className="px-4 py-1 text-xs font-semibold text-gray-500 uppercase tracking-wider">Network</div>
                </div>
                {channels.filter(ch => ch.channel_type === 'dm').map(ch => {
                  const badge = channelBadges[ch.id] || 0;
                  const preview = channelPreviews[ch.id];
                  return (
                    <button
                      key={ch.id}
                      onClick={() => { setActiveChannel(ch); setShowMobileSidebar(false); setExpandedThreads(new Set()); setReplyingTo(null); }}
                      className={`w-full flex items-start gap-3.5 px-4 py-4 rounded-2xl text-left transition-colors active:bg-white/15 ${
                        activeChannel?.id === ch.id ? 'bg-white/10' : 'hover:bg-white/5'
                      }`}
                    >
                      <div className="flex-shrink-0 mt-0.5 w-10 h-10 rounded-full bg-white/5 flex items-center justify-center">
                        <span className="text-base">🤝</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <span className={`text-[15px] font-semibold truncate ${badge > 0 ? 'text-white' : 'text-gray-200'}`}>
                            {ch.name.startsWith('DM: ') ? ch.name.slice(4) : ch.name}
                          </span>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {preview && <span className="text-xs text-gray-500">{shortTime(preview.created_at)}</span>}
                            {badge > 0 && (
                              <span className="min-w-[20px] h-[20px] px-1.5 flex items-center justify-center text-[11px] font-bold rounded-full bg-red-500 text-white">
                                {badge > 99 ? '99+' : badge}
                              </span>
                            )}
                          </div>
                        </div>
                        {preview && (
                          <p className={`text-[13px] truncate mt-1 ${badge > 0 ? 'text-gray-300' : 'text-gray-500'}`}>
                            <span className="font-medium">{preview.sender}:</span> {stripFormatting(preview.content)}
                          </p>
                        )}
                      </div>
                    </button>
                  );
                })}
              </>
            )}
            <div className="pt-3 pb-1">
              <div className="px-4 py-1 text-xs font-semibold text-gray-500 uppercase tracking-wider">Support</div>
            </div>
            {(() => {
              const supportCh = channels.find(c => c.channel_type === 'support' && c.talent_id === talentId);
              const badge = supportCh ? (channelBadges[supportCh.id] || 0) : 0;
              const preview = supportCh ? channelPreviews[supportCh.id] : undefined;
              return (
                <button
                  onClick={async () => {
                    const ch = await getOrCreateSupportChannel();
                    if (ch) { setActiveChannel(ch); setShowMobileSidebar(false); setExpandedThreads(new Set()); setReplyingTo(null); }
                  }}
                  className={`w-full flex items-start gap-3.5 px-4 py-4 rounded-2xl text-left transition-colors active:bg-white/15 ${
                    activeChannel?.channel_type === 'support' ? 'bg-white/10' : 'hover:bg-white/5'
                  }`}
                >
                  <div className="flex-shrink-0 mt-0.5 w-10 h-10 rounded-full bg-white/5 flex items-center justify-center">
                    <ChatBubbleLeftRightIcon className="h-5 w-5 text-green-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className={`text-[15px] font-semibold truncate ${badge > 0 ? 'text-white' : 'text-gray-200'}`}>ShoutOut Support</span>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {preview && <span className="text-xs text-gray-500">{shortTime(preview.created_at)}</span>}
                        {badge > 0 && (
                          <span className="min-w-[20px] h-[20px] px-1.5 flex items-center justify-center text-[11px] font-bold rounded-full bg-red-500 text-white">
                            {badge > 99 ? '99+' : badge}
                          </span>
                        )}
                      </div>
                    </div>
                    {preview ? (
                      <p className={`text-[13px] truncate mt-1 ${badge > 0 ? 'text-gray-300' : 'text-gray-500'}`}>
                        <span className="font-medium">{preview.sender}:</span> {stripFormatting(preview.content)}
                      </p>
                    ) : (
                      <p className="text-[13px] text-gray-500 mt-1">Message the ShoutOut team</p>
                    )}
                  </div>
                </button>
              );
            })()}
          </div>
        </div>

        {/* Main chat area */}
        <div className={`${showMobileSidebar ? 'hidden lg:flex' : 'flex'} flex-col flex-1 min-w-0`}>
          {/* Messages */}
          <div ref={messagesContainerRef} className="flex-1 overflow-y-auto">
            {loadingMessages ? (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent" />
              </div>
            ) : (
              <div className="py-2">
                {messages.length === 0 && (
                  <div className="text-center text-gray-500 text-sm py-12">No messages yet. Start the conversation!</div>
                )}
                {messages.map(m => renderMessage(m))}
              </div>
            )}
          </div>

          {/* Input area */}
          {chatBanned && !isAdmin && activeChannel?.channel_type !== 'announcements' && (
            <div
              className="px-4 py-3 border-t border-white/10 bg-red-950/40 text-center"
              style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)' }}
            >
              <p className="text-sm font-medium text-red-300">
                You're temporarily banned from posting in chat.
              </p>
              <p className="mt-0.5 text-xs text-red-400/80">
                If you believe this is a mistake, contact ShoutOut support.
              </p>
            </div>
          )}
          {!chatBanned && (activeChannel?.channel_type !== 'announcements' || isAdmin) && (
            <div className="px-4 py-3 border-t border-white/10 bg-black/40" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)' }}>
              {/* GIF picker */}
              {showGifPicker && (
                <div className="mb-3 bg-gray-900 border border-white/10 rounded-xl p-3 max-h-80 overflow-y-auto">
                  <input
                    type="text"
                    value={gifSearch}
                    onChange={e => setGifSearch(e.target.value)}
                    placeholder="Search GIFs..."
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-[16px] text-white placeholder-gray-500 focus:outline-none focus:border-white/20 mb-2"
                  />
                  {gifLoading ? (
                    <div className="text-center py-4"><div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-500 border-t-transparent mx-auto" /></div>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {gifResults.map(gif => (
                        <button key={gif.id} onClick={() => sendGif(gif)} className="rounded-lg overflow-hidden hover:ring-2 hover:ring-blue-500 transition-all">
                          <img src={gif.images.fixed_height_small.url} alt={gif.title} className="w-full h-auto" loading="lazy" />
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="text-right mt-1"><span className="text-[10px] text-gray-600">Powered by GIPHY</span></div>
                </div>
              )}
              {/* Emoji picker */}
              {showEmojiPicker && (
                <div className="mb-3 bg-gray-900 border border-white/10 rounded-xl p-3 flex flex-wrap gap-2">
                  {EMOJI_LIST.map(emoji => (
                    <button
                      key={emoji}
                      onClick={() => { setMessageInput(prev => prev + emoji); setShowEmojiPicker(false); inputRef.current?.focus(); }}
                      className="text-2xl hover:scale-125 transition-transform p-1"
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              )}
              {/* @mention autocomplete */}
              {mentionSearch !== null && mentionResults.length > 0 && (
                <div className="mb-2 bg-gray-900 border border-white/10 rounded-xl overflow-hidden">
                  {mentionResults.map(t => (
                    <button key={t.id} onClick={() => insertMention(t.name)}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-white/10 text-left ${t.id === '__everyone__' ? 'text-yellow-300' : 'text-gray-200'}`}>
                      <span className={t.id === '__everyone__' ? 'text-yellow-400' : 'text-blue-400'}>@</span>{t.name}
                      {t.id === '__everyone__' && <span className="ml-auto text-[10px] text-yellow-400/60 uppercase">notify all</span>}
                    </button>
                  ))}
                </div>
              )}
              {/* #channel autocomplete — admin only */}
              {channelMentionSearch !== null && channelMentionResults.length > 0 && (
                <div className="mb-2 bg-gray-900 border border-white/10 rounded-xl overflow-hidden">
                  {channelMentionResults.map(c => (
                    <button key={c.id} onClick={() => insertChannelMention(c.name)}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-white/10 text-left text-gray-200">
                      {c.channel_type === 'announcements'
                        ? <MegaphoneIcon className="h-4 w-4 text-yellow-400 flex-shrink-0" />
                        : <HashtagIcon className="h-4 w-4 text-blue-400 flex-shrink-0" />}
                      <span>{c.name}</span>
                      <span className="ml-auto text-[10px] text-gray-500 uppercase">{c.channel_type}</span>
                    </button>
                  ))}
                </div>
              )}
              {/* Replying to bar */}
              {replyingTo && (
                <div className="flex items-center gap-2 px-1 py-1.5 animate-[fadeSlideIn_0.15s_ease-out]">
                  <div className="flex-1 flex items-center gap-2 text-xs text-blue-400">
                    <ArrowUturnLeftIcon className="h-3.5 w-3.5 flex-shrink-0" />
                    <span>Replying to <b>{getSenderInfo(messages.find(m => m.id === replyingTo) || Object.values(threadReplies).flat().find(r => r.id === replyingTo) || {} as Message).name || 'message'}</b></span>
                  </div>
                  <button onClick={() => setReplyingTo(null)} className="p-1 text-gray-500 hover:text-white rounded transition-colors">
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
              )}
              <div className="flex items-center gap-2">
                {activeChannel?.channel_type === 'support' && (
                  <>
                    <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingImage}
                      className="p-2 rounded-lg transition-colors text-gray-500 hover:text-white hover:bg-white/10 disabled:opacity-30"
                    >
                      {uploadingImage ? (
                        <div className="h-5 w-5 animate-spin rounded-full border-2 border-green-400 border-t-transparent" />
                      ) : (
                        <PhotoIcon className="h-5 w-5" />
                      )}
                    </button>
                  </>
                )}
                <button onClick={() => { setShowGifPicker(!showGifPicker); setShowEmojiPicker(false); }} className={`p-2 rounded-lg transition-colors ${showGifPicker ? 'bg-blue-500/20 text-blue-400' : 'text-gray-500 hover:text-white hover:bg-white/10'}`}>
                  <GifIcon className="h-5 w-5" />
                </button>
                <button onClick={() => { setShowEmojiPicker(!showEmojiPicker); setShowGifPicker(false); }} className={`p-2 rounded-lg transition-colors ${showEmojiPicker ? 'bg-blue-500/20 text-blue-400' : 'text-gray-500 hover:text-white hover:bg-white/10'}`}>
                  <FaceSmileIcon className="h-5 w-5" />
                </button>
                <textarea
                  ref={inputRef}
                  rows={1}
                  value={messageInput}
                  onChange={e => { handleInputChange(e as any); e.target.style.height = 'auto'; e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px'; }}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                  placeholder={replyingTo ? 'Type a reply...' : `Message #${activeChannel?.name || ''}`}
                  className={`flex-1 bg-white/5 border rounded-xl px-4 py-2.5 text-[16px] text-white placeholder-gray-500 focus:outline-none transition-colors resize-none ${replyingTo ? 'border-blue-500/40 focus:border-blue-500/60' : 'border-white/10 focus:border-white/20'}`}
                  style={{ maxHeight: '120px' }}
                />
                <button
                  onClick={() => sendMessage()}
                  disabled={!messageInput.trim() || sending}
                  className="p-2.5 rounded-xl bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-30 disabled:hover:bg-blue-500 transition-all active:scale-95"
                >
                  <PaperAirplaneIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          )}
          {activeChannel?.channel_type === 'announcements' && !isAdmin && (
            <div className="px-4 py-3 border-t border-white/10 bg-black/40 text-center text-sm text-gray-500" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
              Only admins can post in announcements. React to messages with emoji!
            </div>
          )}
        </div>
      </div>

      {/* Profile Card Modal */}
      {profileCard && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={() => setProfileCard(null)}>
          <div className="absolute inset-0 bg-black/60" />
          <div className="relative bg-[#111] border border-white/15 rounded-2xl w-full max-w-xs shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
            <button onClick={() => setProfileCard(null)} className="absolute top-3 right-3 text-gray-500 hover:text-white z-10">
              <XMarkIcon className="h-5 w-5" />
            </button>
            <div className="p-6 text-center">
              <ResilientAvatar
                src={profileCard.avatar}
                name={profileCard.name}
                imgClass="w-20 h-20 rounded-full object-cover mx-auto mb-3 border-2 border-white/10"
                fallbackClass="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-2xl font-bold mx-auto mb-3"
              />
              <h3 className="text-white font-bold text-lg">{profileCard.name}</h3>
              {profileCard.slug && (
                <a href={`https://shoutout.fans/${profileCard.slug}`} target="_blank" rel="noopener noreferrer"
                  className="text-blue-400 text-sm hover:text-blue-300 transition-colors">
                  shoutout.fans/{profileCard.slug}
                </a>
              )}
              {profileCard.bio && <p className="text-gray-400 text-sm mt-2 line-clamp-3">{profileCard.bio}</p>}
            </div>
            {profileCard.socials.length > 0 && (
              <div className="px-6 pb-5 flex flex-wrap justify-center gap-2">
                {profileCard.socials.map(s => {
                  const url = SOCIAL_URLS[s.platform]?.(s.handle);
                  return url ? (
                    <a key={s.platform} href={url} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-white/5 border border-white/10 rounded-full text-xs text-gray-300 hover:bg-white/10 hover:text-white transition-colors">
                      <span>{SOCIAL_ICONS[s.platform] || '🔗'}</span>
                      <span className="capitalize">{s.platform}</span>
                    </a>
                  ) : null;
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>,
    document.body,
  );
}
